import { Donut } from "@/components/ui/baseUi/donut"
import { MonthPickerCalendar } from "@/components/ui/monthPickerCalendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { Text } from "@/components/ui/baseUi/text"
import { Toggle } from "@/components/ui/baseUi/toggle"
import { YearPicker } from "@/components/ui/yearPicker"
import { useInfiniteScroll } from "@/modules/core/hooks/useInfiniteScroll"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { SESSION_HISTORY_STATUS_FILTER } from "@/modules/members/gateway/memberApiGateway"
import { SESSION_HISTORY_STATUS } from "@/modules/members/gateway/memberDto"
import {
  useMemberSessionHistoryList,
  useMemberSessionHistorySummary,
} from "@/modules/members/hooks/useMemberSessionHistory"
import type { MemberSessionHistoryItemModel } from "@/modules/members/models/memberSessionHistoryModel"
import ChatEmpty from "@images/empty/chat_empty.svg"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import dayjs from "dayjs"
import { CalendarIcon, CircleCheck, UserRoundX } from "lucide-react"
import { Suspense, useMemo, useState } from "react"

enum VIEW_MODE {
  YEAR = "year",
  MONTH = "month",
}

// ────────────────────────────────── Status Badge ──────────────────────────────────

function StatusBadge({ status }: { status: SESSION_HISTORY_STATUS }) {
  switch (status) {
    case SESSION_HISTORY_STATUS.PRESENT:
      return (
        <div className="flex items-center gap-0.5">
          <CircleCheck className="size-4 text-textOnSurface-positive" />
          <Text fontStyle="LXS" className="font-semibold text-textOnSurface-positive">
            {word("box.member.table.history.status.attended")}
          </Text>
        </div>
      )
    case SESSION_HISTORY_STATUS.NO_SHOW:
      return (
        <div className="flex items-center gap-0.5">
          <UserRoundX className="size-4 text-textOnSurface-negative" />
          <Text fontStyle="LXS" className="font-semibold text-textOnSurface-negative">
            {word("box.member.table.history.status.noShow")}
          </Text>
        </div>
      )
  }
}

// ────────────────────────────────── Filter Chip ──────────────────────────────────

type FilterChipProps = {
  label: string
  count: number
  isActive: boolean
  onClick: () => void
  icon?: React.ReactNode
} & React.ComponentProps<typeof Toggle>

function FilterChip({ label, count, isActive, onClick, icon, ...props }: FilterChipProps) {
  return (
    <Toggle onPressedChange={onClick} pressed={isActive} variant="emphasisLow" size="sm" {...props}>
      {icon && <span className="flex size-3 items-center justify-center">{icon}</span>}
      <Text fontStyle="LS">{label}</Text>
      <span
        className={cn(
          "flex h-4 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-semibold text-textOnSurface-subtle",
          isActive ? "bg-surface-background" : "bg-surface-background-secondary",
        )}
      >
        {count}
      </span>
    </Toggle>
  )
}

// ────────────────────────────────── Session Row ──────────────────────────────────

function SessionRow({ session, isOdd }: { session: MemberSessionHistoryItemModel; isOdd: boolean }) {
  return (
    <div
      className={cn(
        "flex items-start gap-2 px-4 py-2",
        isOdd ? "bg-surface-background-primary-information-default" : "bg-surface-default",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-1">
          <div className="size-4 shrink-0 rounded-full" style={{ backgroundColor: session.programColor }} />
          <Text fontStyle="LM" className="min-w-0 shrink truncate font-semibold text-textOnSurface-default">
            {session.programName}
          </Text>
        </div>
        <div className="flex items-center gap-2 pl-5">
          <Text fontStyle="LS" className="font-semibold text-textOnSurface-subtle">
            {session.formattedDate}
          </Text>
          <Text fontStyle="LS" className="font-semibold text-textOnSurface-subtle">
            {session.timeRange}
          </Text>
        </div>
      </div>
      <div className="flex flex-1 flex-col items-end gap-1 overflow-hidden pt-0.5">
        <StatusBadge status={session.status} />
        <Text fontStyle="LS" className="w-full truncate text-right font-semibold text-textOnSurface-subtle">
          {session.offerName}
        </Text>
      </div>
    </div>
  )
}

function SessionRowSkeleton({ isOdd }: { isOdd: boolean }) {
  return (
    <div
      className={cn(
        "flex items-start gap-2 px-4 py-2",
        isOdd ? "bg-surface-background-primary-information-default" : "bg-surface-default",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-1">
          <Skeleton className="size-4 shrink-0 rounded-full" />
          <Skeleton className="h-4 w-24 rounded-md" />
        </div>
        <div className="flex items-center gap-2 pl-5">
          <Skeleton className="h-3 w-16 rounded-sm" />
          <Skeleton className="h-3 w-12 rounded-sm" />
        </div>
      </div>
      <div className="flex flex-1 flex-col items-end gap-1 overflow-hidden pt-0.5">
        <Skeleton className="h-5 w-20 rounded-full" />
        <Skeleton className="h-3 w-32 rounded-sm" />
      </div>
    </div>
  )
}

export function MemberTabHistorySessionListSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden">
      {Array.from({ length: 20 }).map((_, i) => (
        <SessionRowSkeleton key={i} isOdd={i % 2 !== 0} />
      ))}
    </div>
  )
}

// ────────────────────────────────── Main Component ──────────────────────────────────

type MemberTabHistoryProps = {
  memberId: string
  className?: string
} & React.ComponentProps<"div">

export function MemberTabHistory({ memberId, className, ...props }: MemberTabHistoryProps) {
  const { result: box } = useCurrentBox()
  const [statusFilter, setStatusFilter] = useState<SESSION_HISTORY_STATUS_FILTER>(SESSION_HISTORY_STATUS_FILTER.ALL)
  const [viewMode, setViewMode] = useState<VIEW_MODE>(VIEW_MODE.MONTH)
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [hoveredProgram, setHoveredProgram] = useState<string | null>(null)

  const year = selectedDate.year()
  const month = viewMode === VIEW_MODE.MONTH ? selectedDate.month() + 1 : null // backend use 1-12 for month unlike dayjs 0 indexed month

  const YEARS_RANGE = useMemo(() => {
    const currentYear = dayjs().year()
    const boxCreationYear = box?.createdAt ? dayjs(box.createdAt).year() : currentYear - 4
    const yearsCount = currentYear - boxCreationYear + 1
    return Array.from({ length: yearsCount }, (_, i) => currentYear - i)
  }, [box])

  const { summary } = useMemberSessionHistorySummary(memberId, year, month)

  const handleMonthSelect = (monthIndex: number) => {
    setSelectedDate(dayjs().year(selectedDate.year()).month(monthIndex))
    setIsPickerOpen(false)
  }

  const handleYearChange = (yearValue: string) => {
    setSelectedDate(selectedDate.year(Number(yearValue)))
    setIsPickerOpen(false)
  }

  const dateLabel = viewMode === VIEW_MODE.MONTH ? selectedDate.format("MMMM YYYY") : selectedDate.format("YYYY")

  return (
    <div className={cn("flex w-full flex-col gap-4 px-6 pt-4", className)} {...props}>
      {/* ── Date picker & View mode toggle ── */}
      <div className="flex items-center justify-between">
        {viewMode === VIEW_MODE.MONTH ? (
          <div className="pt-3 pb-2">
            <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
              <PopoverTrigger
                render={
                  <button className="group flex cursor-pointer items-center gap-1.5 rounded-full px-1 py-0.5">
                    <Text fontStyle="LL" className="text-textOnSurface-subtle group-hover:text-textOnSurface-default">
                      {dateLabel}
                    </Text>
                    <CalendarIcon className="size-4 text-textOnSurface-subtle group-hover:text-textOnSurface-default" />
                  </button>
                }
              />
              <PopoverContent className="w-64 rounded-2xl border-none bg-surface-background-float p-4" align="start">
                <MonthPickerCalendar
                  year={selectedDate.year()}
                  month={selectedDate.month()}
                  onSelectMonth={handleMonthSelect}
                  minDate={box?.createdAt ? new Date(box.createdAt) : undefined}
                />
              </PopoverContent>
            </Popover>
          </div>
        ) : (
          <YearPicker
            year={selectedDate.year()}
            onYearChange={handleYearChange}
            years={YEARS_RANGE}
            className="w-auto gap-1 border-none bg-surface-background"
          ></YearPicker>
        )}

        <div className="flex gap-2.5 rounded-2xl bg-surface-background-secondary p-1">
          <button
            type="button"
            onClick={() => setViewMode(VIEW_MODE.MONTH)}
            className={cn(
              "flex h-5 items-center rounded-full px-2 text-xs transition-colors",
              viewMode === VIEW_MODE.MONTH
                ? "bg-interactive-background-neutral-faded-highlighted font-medium text-textOnSurface-default"
                : "cursor-pointer text-textOnSurface-subtle hover:text-textOnSurface-default",
            )}
          >
            {word("box.member.table.history.viewMode.month")}
          </button>
          <button
            type="button"
            onClick={() => setViewMode(VIEW_MODE.YEAR)}
            className={cn(
              "flex h-5 items-center rounded-full px-2 text-xs transition-colors",
              viewMode === VIEW_MODE.YEAR
                ? "bg-interactive-background-neutral-faded-highlighted font-medium text-textOnSurface-default"
                : "cursor-pointer text-textOnSurface-subtle hover:text-textOnSurface-default",
            )}
          >
            {word("box.member.table.history.viewMode.year")}
          </button>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className="flex gap-2">
        {/* Sessions summary */}
        <div className="flex min-w-0 flex-3 flex-col gap-2 rounded-2xl bg-surface-background-secondary px-4 py-2">
          <Text fontStyle="LM" className="font-semibold text-textOnSurface-default">
            {summary.totalSessions === 0
              ? word("box.member.table.history.summary.empty.title")
              : `${summary.totalSessions} ${word("box.member.table.history.sessionCount", { count: summary.totalSessions })} ${
                  viewMode === VIEW_MODE.MONTH
                    ? word("box.member.table.history.thisMonth")
                    : word("box.member.table.history.thisYear")
                }`}
          </Text>
          <div className="flex flex-1">
            <div className="flex w-1/2 flex-col gap-3">
              {summary.programs.length === 0 && (
                <div className="flex items-center gap-0.5">
                  <div className="size-1 shrink-0 rounded-full bg-interactive-border-neutral-highlighted" />
                  <Text fontStyle="LS" className="text-textOnSurface-muted">
                    --
                  </Text>
                </div>
              )}
              {summary.programs.map((p) => {
                const isHovered = hoveredProgram === p.name

                return (
                  <div key={p.name} className="flex min-w-0 items-center gap-1">
                    <div className="size-1 shrink-0 rounded-full" style={{ backgroundColor: p.color }} />
                    <Text
                      fontStyle="LS"
                      className={cn(
                        "shrink-0 text-textOnSurface-muted transition-colors",
                        isHovered && "text-textOnSurface-default",
                      )}
                    >
                      {p.count}
                    </Text>
                    <Text
                      fontStyle="LS"
                      className={cn(
                        "min-w-0 shrink truncate text-textOnSurface-muted transition-colors",
                        isHovered && "text-textOnSurface-default",
                      )}
                    >
                      {p.name}
                    </Text>
                    <Text
                      fontStyle="LS"
                      className={cn(
                        "shrink-0 text-textOnSurface-muted transition-colors",
                        isHovered && "text-textOnSurface-default",
                      )}
                    >
                      ({p.percentage}%)
                    </Text>
                  </div>
                )
              })}
            </div>
            <div className="bg-pink flex flex-1 items-center justify-center p-2 pr-0">
              <Donut
                size={90}
                strokeWidth={20}
                slices={summary.programs.map((p) => ({ id: p.name, value: p.count, color: p.color }))}
                onSliceHover={setHoveredProgram}
              />
            </div>
          </div>
        </div>

        {/* Favorite slots */}
        <div className="flex min-w-0 flex-2 flex-col gap-2 rounded-2xl bg-surface-background-secondary px-4 py-2">
          <Text fontStyle="LM" className="font-semibold text-textOnSurface-default">
            {summary.totalSessions === 0
              ? word("box.member.table.history.favoriteSlots.empty")
              : word("box.member.table.history.favoriteSlots.label")}
          </Text>
          <div className="flex flex-1 flex-col">
            {summary.favoriteSlots.length === 0 && (
              <div className="flex items-start justify-between">
                <div className="flex gap-1">
                  <Text fontStyle="LS" className="text-textOnSurface-muted">
                    1/
                  </Text>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-0.5">
                      <div className="size-1 shrink-0 rounded-full bg-interactive-border-neutral-highlighted" />
                      <Text fontStyle="LS" className="text-textOnSurface-default">
                        --
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {summary.favoriteSlots.map((slot, index) => (
              <div key={`${slot.programName}-${slot.dayOfWeek}`} className="flex items-start justify-between">
                <div className="flex min-w-0 items-center gap-1">
                  <Text fontStyle="LS" className="shrink-0 self-start text-textOnSurface-subtle">
                    {index + 1}/
                  </Text>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex min-w-0 items-center gap-0.5">
                      <div className="size-1 shrink-0 rounded-full" style={{ backgroundColor: slot.programColor }} />
                      <Text fontStyle="LS" className="min-w-0 shrink truncate text-textOnSurface-default">
                        {slot.programName}
                      </Text>
                    </div>
                    <Text fontStyle="LS" className="pl-1.5 text-textOnSurface-default">
                      {word(`global.day.${slot.dayOfWeek}`)} {slot.timeRange}
                    </Text>
                  </div>
                </div>
                <Text fontStyle="LS" className="text-textOnSurface-muted">
                  x{slot.count}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Filter chips ── */}
      <div className="flex gap-2 pt-2">
        <FilterChip
          label={word("box.member.table.history.filter.all")}
          count={summary.totalSessions}
          isActive={statusFilter === SESSION_HISTORY_STATUS_FILTER.ALL}
          onClick={() => setStatusFilter(SESSION_HISTORY_STATUS_FILTER.ALL)}
        />
        <FilterChip
          label={word("box.member.table.history.status.attended")}
          count={summary.attendedCount}
          isActive={statusFilter === SESSION_HISTORY_STATUS_FILTER.PRESENT}
          onClick={() => setStatusFilter(SESSION_HISTORY_STATUS_FILTER.PRESENT)}
          icon={<CircleCheck className="size-3" />}
        />
        <FilterChip
          label={word("box.member.table.history.status.noShow")}
          count={summary.noShowCount}
          isActive={statusFilter === SESSION_HISTORY_STATUS_FILTER.NO_SHOW}
          onClick={() => setStatusFilter(SESSION_HISTORY_STATUS_FILTER.NO_SHOW)}
          icon={<UserRoundX className="size-3" />}
        />
      </div>

      {/* ── Session list ── */}
      <Suspense fallback={<MemberTabHistorySessionListSkeleton />}>
        <MemberTabHistorySessionList memberId={memberId} year={year} month={month} statusFilter={statusFilter} />
      </Suspense>
    </div>
  )
}

const MemberTabHistorySessionList = ({
  memberId,
  year,
  month,
  statusFilter,
}: {
  memberId: string
  year: number
  month: number | null
  statusFilter: SESSION_HISTORY_STATUS_FILTER
}) => {
  const { sessions, fetchNextPage, hasNextPage, isFetchingNextPage } = useMemberSessionHistoryList(
    memberId,
    year,
    month,
    statusFilter,
  )

  const { InfiniteScrollTrigger } = useInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  })

  return (
    <div className="flex flex-col overflow-hidden">
      {sessions.length === 0 ? (
        <div className="flex items-center pl-4">
          <div className="flex flex-1 flex-col items-center gap-2 py-8">
            <Text fontStyle="TM" className="text-center text-textOnSurface-default">
              {statusFilter === SESSION_HISTORY_STATUS_FILTER.PRESENT
                ? word("box.member.table.history.empty.attended.title")
                : statusFilter === SESSION_HISTORY_STATUS_FILTER.NO_SHOW
                  ? word("box.member.table.history.empty.noShow.title")
                  : word("box.member.table.history.empty.title")}
            </Text>
            <Text fontStyle="BS" className="text-center text-textOnSurface-subtle">
              {statusFilter === SESSION_HISTORY_STATUS_FILTER.PRESENT
                ? word("box.member.table.history.empty.attended.description")
                : statusFilter === SESSION_HISTORY_STATUS_FILTER.NO_SHOW
                  ? word("box.member.table.history.empty.noShow.description")
                  : word("box.member.table.history.empty.description")}
            </Text>
          </div>
          <img src={ChatEmpty} alt="" className="h-38 w-auto flex-1" />
        </div>
      ) : (
        <>
          {sessions.map((session, index) => (
            <SessionRow key={session.id} session={session} isOdd={index % 2 !== 0} />
          ))}
          <InfiniteScrollTrigger />
        </>
      )}
    </div>
  )
}

export const MemberTabHistoryLoading = () => {
  return (
    <div className={cn("flex w-full flex-col gap-4 px-4 pt-6")}>
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-28 rounded-2xl" />
        <div className="flex gap-2">
          <Skeleton className="h-7 w-16 rounded-full" />
          <Skeleton className="h-7 w-20 rounded-full" />
          <Skeleton className="h-7 w-20 rounded-full" />
        </div>
        <MemberTabHistorySessionListSkeleton />
      </div>
    </div>
  )
}
